import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ApiError } from '../api/client'
import { useCostDrivers, useCreateCostDriver, useOrganizationSettings } from '../api/hooks'
import type { CostDriver, DriverHealthState, DriverStatus, DriverType } from '../api/types'
import { formatPercent } from '../components/Badges'

/**
 * Screen 3 — Cost Driver List (§10).
 *
 * The health column is the point of this screen: which drivers are incomplete and why,
 * at a glance. It is computed live from the definitions rather than waiting on a QC
 * run, because the moment an analyst most needs it is while they are still building the
 * driver — before any run has happened.
 */
const HEALTH: Record<
  DriverHealthState,
  { label: string; bg: string; fg: string; hint: string }
> = {
  Ok: {
    label: 'Ok',
    bg: 'var(--color-ok-wash)',
    fg: 'var(--color-ok)',
    hint: 'Effective allocations total exactly 100.',
  },
  Incomplete: {
    label: 'Incomplete',
    bg: 'var(--color-error-wash)',
    fg: 'var(--color-error)',
    hint: 'Allocations do not total 100. QC-D001 will block any run using this driver.',
  },
  NoAllocations: {
    label: 'No allocations',
    bg: 'var(--color-error-wash)',
    fg: 'var(--color-error)',
    hint: 'No effective allocation rows at all. QC-D002 will block any run using this driver.',
  },
  Unevaluated: {
    label: 'Unevaluated',
    bg: 'var(--color-panel-alt)',
    fg: 'var(--color-muted)',
    hint: 'A SQL driver: its targets cannot be known without executing the query.',
  },
  Retired: {
    label: 'Retired',
    bg: 'var(--color-panel-alt)',
    fg: 'var(--color-muted)',
    hint: 'Deliberately out of service.',
  },
}

const HEALTH_FILTERS: { value: DriverHealthState | null; label: string }[] = [
  { value: null, label: 'All' },
  { value: 'Incomplete', label: 'Incomplete' },
  { value: 'NoAllocations', label: 'Empty' },
  { value: 'Ok', label: 'Ok' },
]

export function CostDriverList() {
  const [health, setHealth] = useState<DriverHealthState | null>(null)
  const [type, setType] = useState<DriverType | null>(null)
  const [status, setStatus] = useState<DriverStatus | null>(null)
  const [creating, setCreating] = useState(false)

  const { data: drivers = [], isLoading } = useCostDrivers(type, status, health)
  const { data: allDrivers = [] } = useCostDrivers()
  const navigate = useNavigate()

  const counts = HEALTH_FILTERS.reduce<Record<string, number>>((acc, f) => {
    acc[f.label] =
      f.value === null ? allDrivers.length : allDrivers.filter((d) => d.healthState === f.value).length
    return acc
  }, {})

  return (
    <div className="flex h-full flex-col">
      <div
        className="flex flex-wrap items-center gap-3 border-b px-4 py-2"
        style={{ background: 'var(--color-panel)', borderColor: 'var(--color-rule-soft)' }}
      >
        <h1 className="text-[14px] font-semibold">Cost Drivers</h1>

        <div className="flex gap-0.5">
          {HEALTH_FILTERS.map((f) => {
            const active = health === f.value
            return (
              <button
                key={f.label}
                onClick={() => setHealth(f.value)}
                className="rounded px-2 py-1 text-[12px]"
                style={{
                  background: active ? 'var(--color-accent)' : 'transparent',
                  color: active ? '#fff' : 'var(--color-muted)',
                  border: `1px solid ${active ? 'var(--color-accent)' : 'var(--color-rule)'}`,
                }}
              >
                {f.label}
                <span className="num ml-1 opacity-70">{counts[f.label] ?? 0}</span>
              </button>
            )
          })}
        </div>

        <select
          className="input w-28"
          value={type ?? ''}
          onChange={(e) => setType((e.target.value || null) as DriverType | null)}
        >
          <option value="">Any type</option>
          <option value="Static">Static</option>
          <option value="Sql">Sql</option>
        </select>

        <select
          className="input w-28"
          value={status ?? ''}
          onChange={(e) => setStatus((e.target.value || null) as DriverStatus | null)}
        >
          <option value="">Any status</option>
          <option value="Draft">Draft</option>
          <option value="Active">Active</option>
          <option value="Retired">Retired</option>
        </select>

        <button className="btn ml-auto" onClick={() => setCreating(true)}>
          New driver
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-auto" style={{ background: 'var(--color-panel)' }}>
        {isLoading ? (
          <p className="p-4 text-[12px]" style={{ color: 'var(--color-muted)' }}>
            Loading…
          </p>
        ) : drivers.length === 0 ? (
          <p className="p-4 text-[12px]" style={{ color: 'var(--color-muted)' }}>
            No drivers match this filter.
          </p>
        ) : (
          <table className="w-full border-collapse">
            <thead className="sticky top-0 z-10" style={{ background: 'var(--color-panel-alt)' }}>
              <tr className="border-b text-left" style={{ borderColor: 'var(--color-rule)' }}>
                <th className="col-head px-3 py-1.5">Driver</th>
                <th className="col-head px-3 py-1.5">Code</th>
                <th className="col-head px-3 py-1.5">Type</th>
                <th className="col-head px-3 py-1.5">Status</th>
                <th className="col-head px-3 py-1.5">Health</th>
                <th className="col-head px-3 py-1.5 text-right">Total</th>
                <th className="col-head px-3 py-1.5 text-right">Centers</th>
                <th className="col-head px-3 py-1.5 text-right">Accounts</th>
                <th className="px-3 py-1.5" />
              </tr>
            </thead>
            <tbody>
              {drivers.map((driver) => (
                <DriverRow
                  key={driver.costDriverId}
                  driver={driver}
                  onEdit={() => navigate(`/drivers/${driver.costDriverId}`)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {creating && <NewDriverDialog onClose={() => setCreating(false)} />}
    </div>
  )
}

function DriverRow({ driver, onEdit }: { driver: CostDriver; onEdit: () => void }) {
  const health = HEALTH[driver.healthState]
  const broken = driver.healthState === 'Incomplete' || driver.healthState === 'NoAllocations'

  return (
    <tr
      className="grid-row border-b"
      style={{ borderColor: 'var(--color-rule-soft)' }}
      tabIndex={0}
      onClick={onEdit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          onEdit()
        }
      }}
    >
      <td className="px-3 py-1 font-medium">{driver.name}</td>
      <td className="ident px-3 py-1" style={{ color: 'var(--color-muted)' }}>
        {driver.code}
      </td>
      <td className="px-3 py-1" style={{ color: 'var(--color-muted)' }}>
        {driver.driverType}
      </td>
      <td className="px-3 py-1">
        <span
          className="badge"
          style={{
            background:
              driver.status === 'Active' ? 'var(--color-ok-wash)' : 'var(--color-panel-alt)',
            color: driver.status === 'Active' ? 'var(--color-ok)' : 'var(--color-muted)',
          }}
        >
          {driver.status}
        </span>
      </td>
      <td className="px-3 py-1">
        <span className="badge" style={{ background: health.bg, color: health.fg }} title={health.hint}>
          {health.label}
        </span>
      </td>
      <td
        className="num px-3 py-1"
        style={{ color: broken ? 'var(--color-error)' : 'var(--color-muted)' }}
        title={broken ? `Gap to 100: ${driver.gapPercent.toFixed(6)}` : undefined}
      >
        {/* Neither a SQL nor a patient-days driver has stored percentages to total.
            Showing 0.00% would read as a broken driver rather than a different kind. */}
        {driver.driverType === 'Static' ? formatPercent(driver.totalPercent) : '—'}
      </td>
      <td className="num px-3 py-1" style={{ color: 'var(--color-muted)' }}>
        {driver.driverType === 'Static' ? driver.costCenterCount : '—'}
      </td>
      <td
        className="num px-3 py-1"
        style={{
          // Zero accounts on an Active driver is QC-D014 — defined but referenced by
          // nothing. Informational, not a fault, so it stays muted rather than red.
          color:
            driver.accountMapCount === 0 && driver.status === 'Active'
              ? 'var(--color-warn)'
              : 'var(--color-muted)',
        }}
        title={
          driver.accountMapCount === 0 && driver.status === 'Active'
            ? 'Active but no account routes through it (QC-D014).'
            : undefined
        }
      >
        {driver.accountMapCount}
      </td>
      <td className="px-3 py-1 text-right">
        <button
          className="text-[11px] underline"
          style={{ color: 'var(--color-accent)' }}
          onClick={(e) => {
            e.stopPropagation()
            onEdit()
          }}
        >
          edit
        </button>
      </td>
    </tr>
  )
}

function NewDriverDialog({ onClose }: { onClose: () => void }) {
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [driverType, setDriverType] = useState<DriverType>('Static')
  const { data: settings } = useOrganizationSettings()
  const ppdEnabled = settings?.ppdModeEnabled ?? false
  const [violations, setViolations] = useState<Record<string, string>>({})
  const create = useCreateCostDriver()
  const navigate = useNavigate()

  function submit(e: React.FormEvent) {
    e.preventDefault()
    setViolations({})
    create.mutate(
      {
        code: code.trim(),
        name: name.trim(),
        description: description.trim() || null,
        driverType,
      },
      {
        onSuccess: (driver) => {
          onClose()
          navigate(`/drivers/${driver.costDriverId}`)
        },
        onError: (err) =>
          setViolations(
            err instanceof ApiError
              ? Object.keys(err.violations).length > 0
                ? err.violations
                : { _: err.message }
              : { _: String(err) },
          ),
      },
    )
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(26, 36, 48, 0.35)' }}
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-96 rounded border p-4"
        style={{ background: 'var(--color-panel)', borderColor: 'var(--color-ink)' }}
      >
        <h2 className="mb-3 text-[15px] font-semibold">New cost driver</h2>

        <label className="mb-3 flex flex-col gap-1">
          <span className="col-head">code</span>
          <input
            className="input input-mono"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoFocus
          />
          {violations.code && (
            <span className="text-[11px]" style={{ color: 'var(--color-error)' }}>
              {violations.code}
            </span>
          )}
        </label>

        <label className="mb-3 flex flex-col gap-1">
          <span className="col-head">name</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
          {violations.name && (
            <span className="text-[11px]" style={{ color: 'var(--color-error)' }}>
              {violations.name}
            </span>
          )}
        </label>

        <label className="mb-3 flex flex-col gap-1">
          <span className="col-head">description</span>
          <input
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>

        <label className="mb-1 flex flex-col gap-1">
          <span className="col-head">type</span>
          <select
            className="input"
            value={driverType}
            onChange={(e) => setDriverType(e.target.value as DriverType)}
          >
            <option value="Static">Static — hand-entered percentages</option>
            <option value="Sql">Sql — a query returning raw weights</option>
            {/* Offered only in PPD mode: without a census there is nothing to
                allocate in proportion to, and the API refuses it. */}
            {ppdEnabled && (
              <option value="PatientDays">Patient days — each department's share of occupancy</option>
            )}
          </select>
        </label>
        <p className="mb-3 text-[11px]" style={{ color: 'var(--color-faint)' }}>
          Fixed at creation. Switching later would strand whichever definition already
          exists.
          {driverType === 'PatientDays' &&
            ' Percentages come from the occupancy ledger and move with it — there is nothing to enter here.'}
        </p>

        {violations._ && (
          <p className="mb-2 text-[12px]" style={{ color: 'var(--color-error)' }}>
            {violations._}
          </p>
        )}

        <div className="flex gap-2">
          <button type="submit" className="btn btn-primary" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create'}
          </button>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  )
}
